import React from 'react';
import { DrawnCard } from './types';

interface CardDisplayProps {
  drawnCard: DrawnCard;
  onFlip: () => void;
  onSendToAnvil?: (cardName: string) => void;
}

export const CardDisplay: React.FC<CardDisplayProps> = ({ drawnCard, onFlip, onSendToAnvil }) => {
  const { card, isRevealed, position } = drawnCard;

  return (
    <div className="flex flex-col items-center space-y-3 w-full max-w-sm mx-auto">
      {/* Position Header */}
      <div className="text-center">
        <span className="text-[10px] font-mono text-gold-ancient uppercase tracking-widest block">
          POSITION {position.index + 1}: {position.label}
        </span>
        <span className="text-[11px] font-serif text-parchment-dim italic">
          {position.description}
        </span>
      </div>

      {/* 3D Flip Card Container */}
      <div
        onClick={onFlip}
        role="button"
        tabIndex={0}
        aria-label={`${card.name} - ${isRevealed ? 'Revealed' : 'Face down'}. Click to flip.`}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onFlip();
          }
        }}
        className="group relative w-full h-[460px] cursor-pointer perspective-[1200px] focus:outline-none focus-visible:ring-2 focus-visible:ring-gold-bright rounded-lg"
      >
        <div
          className={`relative w-full h-full duration-700 transition-transform rounded-lg shadow-chthonic [transform-style:preserve-3d] ${
            isRevealed ? '[transform:rotateY(180deg)]' : ''
          }`}
        >
          {/* BACK OF CARD (Face Down) */}
          <div className="absolute inset-0 w-full h-full rounded-lg border-2 border-bronze-base/70 bg-gradient-to-b from-[#121217] via-[#0A0A0C] to-[#16161D] p-6 flex flex-col items-center justify-between [backface-visibility:hidden] group-hover:border-gold-bright transition-colors shadow-[inset_0_0_40px_rgba(0,0,0,0.8)]">
            <div className="text-center space-y-1">
              <span className="text-[9px] font-mono text-gold-ancient/80 uppercase tracking-widest block">
                VAS ORACULI • 80 LEGIONES
              </span>
              <div className="h-[1px] w-24 mx-auto bg-gradient-to-r from-transparent via-bronze-base to-transparent" />
            </div>

            {/* Central Seal Plaque */}
            <div className="relative flex flex-col items-center justify-center p-8 rounded-full border border-bronze-dark/80 bg-obsidian-deep/90 shadow-glow-gold">
              <span className="text-4xl text-gold-bright select-none">{card.sigilSymbol}</span>
              <span className="text-[10px] font-display text-gold-bright tracking-widest uppercase mt-2">
                BELIAL
              </span>
            </div>

            <div className="text-center space-y-1">
              <span className="text-[10px] font-display text-gold-ancient tracking-wider uppercase block">
                TAP TO UNSEAL
              </span>
              <span className="text-[9px] font-mono text-iron-steel">
                SEAL #{card.id.toString().padStart(2, '0')}
              </span>
            </div>
          </div>

          {/* FRONT OF CARD (Face Up / Revealed) */}
          <div className="absolute inset-0 w-full h-full rounded-lg border-2 border-gold-bright/80 bg-gradient-to-b from-[#14141A] via-[#0C0C10] to-[#181822] p-5 sm:p-6 flex flex-col justify-between [transform:rotateY(180deg)] [backface-visibility:hidden] shadow-glow-bronze overflow-y-auto">
            {/* Header Metadata */}
            <div className="space-y-1 border-b border-iron-dark/80 pb-3">
              <div className="flex justify-between items-center text-[10px] font-mono text-gold-ancient uppercase">
                <span>LEGION #{card.id.toString().padStart(2, '0')}</span>
                <span className="text-iron-steel font-serif italic">{card.rank}</span>
              </div>
              <h4 className="font-display text-base sm:text-lg font-bold text-parchment-light uppercase leading-tight">
                {card.name}
              </h4>
              <div className="text-[11px] font-serif text-gold-bright italic">
                {card.cohortName}
              </div>
            </div>

            {/* Counsel: Brutal Verdict */}
            <div className="my-2 rounded border border-bronze-dark/60 bg-obsidian-deep/90 p-3 text-center space-y-1">
              <span className="text-[9px] font-mono text-ember-spark uppercase tracking-widest block">
                SOVEREIGN VERDICT
              </span>
              <p className="font-serif text-xs text-gold-bright font-semibold italic leading-relaxed">
                "{card.counsel.verdict}"
              </p>
            </div>

            {/* Diagnosis & Action */}
            <div className="space-y-2 text-[11px] font-serif text-parchment-base leading-relaxed">
              <div>
                <strong className="font-mono text-[9px] text-iron-steel uppercase block">Diagnosis:</strong>
                <span className="text-parchment-dim">{card.counsel.diagnosis}</span>
              </div>
              <div>
                <strong className="font-mono text-[9px] text-gold-ancient uppercase block">Required Action:</strong>
                <span className="text-parchment-light">{card.counsel.action}</span>
              </div>
              <div>
                <strong className="font-mono text-[9px] text-ember-spark uppercase block">Fatal Warning:</strong>
                <span className="text-ember-flare/90">{card.counsel.warning}</span>
              </div>
            </div>

            {/* Footer Attributes & Send to Anvil */}
            <div className="pt-2 border-t border-iron-dark/80 flex items-center justify-between text-[10px] font-mono text-iron-steel">
              <span>{card.attributes.metal}</span>
              {onSendToAnvil && (
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    onSendToAnvil(card.name);
                  }}
                  className="text-gold-bright hover:underline"
                >
                  Weigh on Anvil →
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CardDisplay;
